import React, { useEffect, useRef, useState } from "react";

/**
 * v1.25.48 — Animated number ticker for admin stat tiles.
 * Interpolates from previous value to new value over `duration` ms using
 * a cubic-ease-out curve, so dashboard numbers "count up" the first time
 * they load and re-count whenever they change. `format` is a callback that
 * turns the current interpolated number into a display string (₹ Cr / L / K,
 * plain integers, etc.), so the ticker plays nicely with any formatter.
 */
export default function CountUp({ to = 0, format = (n) => String(Math.round(n)), duration = 900, testId }) {
    const target = Number(to) || 0;
    const [display, setDisplay] = useState(target);
    const fromRef = useRef(target);
    const rafRef = useRef(null);
    const startRef = useRef(0);

    useEffect(() => {
        cancelAnimationFrame(rafRef.current);
        const from = fromRef.current;
        if (from === target) { setDisplay(target); return; }
        startRef.current = performance.now();
        const step = (now) => {
            const t = Math.min(1, (now - startRef.current) / duration);
            const eased = 1 - Math.pow(1 - t, 3); // cubic ease-out
            const current = from + (target - from) * eased;
            setDisplay(current);
            if (t < 1) rafRef.current = requestAnimationFrame(step);
            else { fromRef.current = target; setDisplay(target); }
        };
        rafRef.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(rafRef.current);
    }, [target, duration]);

    return <span data-testid={testId}>{format(display)}</span>;
}
